<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import { toast } from 'svelte-sonner';
  import momentiumLogo from '../assets/momentium-logo.png';
  import ProfileDropdown from '../components/ProfileDropdown.svelte';
  import MobileTopBar from '../components/mobile/MobileTopBar.svelte';
  import { viewport } from '../lib/stores/viewport';
  import Icon from '../components/Icon.svelte';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';

  interface TrashItem {
    type: 'project' | 'task' | 'event';
    id: string;
    title: string;
    discardedAt: string;
    daysRemaining: number;
    // Project-specific
    category?: string;
    status?: string;
    taskCount?: number;
    // Task-specific
    projectId?: string;
    projectTitle?: string;
    priority?: string;
    // Event-specific
    eventType?: string;
    date?: string;
  }

  interface Props {
    projects: TrashItem[];
    tasks: TrashItem[];
    events: TrashItem[];
  }

  let { projects, tasks, events }: Props = $props();

  // Local state for optimistic updates
  // svelte-ignore state_referenced_locally
  let localProjects = $state<TrashItem[]>([...projects]);
  // svelte-ignore state_referenced_locally
  let localTasks = $state<TrashItem[]>([...tasks]);
  // svelte-ignore state_referenced_locally
  let localEvents = $state<TrashItem[]>([...events]);

  // Track prop identity for re-sync
  // svelte-ignore state_referenced_locally
  let syncedProjectsRef = projects;
  // svelte-ignore state_referenced_locally
  let syncedTasksRef = tasks;
  // svelte-ignore state_referenced_locally
  let syncedEventsRef = events;

  // Re-sync from Inertia props when they change
  // eslint-disable-next-line momentium/no-unguarded-effect -- sync external Inertia props into local state on navigation
  $effect(() => {
    if (projects !== syncedProjectsRef) {
      localProjects = [...projects];
      syncedProjectsRef = projects;
    }
    if (tasks !== syncedTasksRef) {
      localTasks = [...tasks];
      syncedTasksRef = tasks;
    }
    if (events !== syncedEventsRef) {
      localEvents = [...events];
      syncedEventsRef = events;
    }
  });

  // Combine all items sorted by discardedAt (most recent first)
  let allItems = $derived(
    [...localProjects, ...localTasks, ...localEvents].sort(
      (a, b) => new Date(b.discardedAt).getTime() - new Date(a.discardedAt).getTime(),
    ),
  );

  let isEmpty = $derived(allItems.length === 0);

  // Confirmation dialog state
  let confirmingDelete: TrashItem | null = $state(null);
  let confirmingEmptyTrash = $state(false);

  function cancelConfirm() {
    confirmingDelete = null;
  }

  function cancelEmptyTrash() {
    confirmingEmptyTrash = false;
  }

  // Format helpers
  function daysAgo(discardedAt: string): string {
    const days = Math.floor((Date.now() - new Date(discardedAt).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }

  function typeIcon(type: string): string {
    switch (type) {
      case 'project':
        return 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z';
      case 'task':
        return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4';
      case 'event':
        return 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z';
      default:
        return '';
    }
  }

  function typeLabel(type: string): string {
    switch (type) {
      case 'project':
        return 'Project';
      case 'task':
        return 'Task';
      case 'event':
        return 'Event';
      default:
        return type;
    }
  }

  function contextInfo(item: TrashItem): string {
    if (item.type === 'project') {
      const parts: string[] = [];
      if (item.category) parts.push(item.category);
      if (item.status) parts.push(item.status);
      return parts.join(' · ');
    }
    if (item.type === 'task') {
      return item.projectTitle ? `in ${item.projectTitle}` : '';
    }
    if (item.type === 'event') {
      const parts: string[] = [];
      if (item.eventType) parts.push(item.eventType);
      if (item.date) parts.push(item.date);
      return parts.join(' · ');
    }
    return '';
  }

  // Actions
  let restoringIds = $state<Set<string>>(new Set());
  let deletingIds = $state<Set<string>>(new Set());

  function restoreItem(item: TrashItem) {
    let url: string;
    switch (item.type) {
      case 'project':
        url = `/projects/${item.id}/undiscard`;
        break;
      case 'task':
        url = `/projects/${item.projectId}/tasks/${item.id}/undiscard`;
        break;
      case 'event':
        url = `/calendar_events/${item.id}/undiscard`;
        break;
    }

    restoringIds = new Set([...restoringIds, item.id]);

    router.post(
      url,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          removeItemFromLocal(item);
          const extra =
            item.type === 'project' && item.taskCount
              ? ` (${item.taskCount} task${item.taskCount === 1 ? '' : 's'} also restored)`
              : '';
          toast(`${typeLabel(item.type)} restored${extra}`);
        },
        onError: () => {
          toast.error('Failed to restore item');
        },
        onFinish: () => {
          const next = new Set(restoringIds);
          next.delete(item.id);
          restoringIds = next;
        },
      },
    );
  }

  function requestDeleteItem(item: TrashItem) {
    confirmingDelete = item;
  }

  function confirmDeleteItem() {
    if (!confirmingDelete) return;
    const item = confirmingDelete;
    confirmingDelete = null;

    deletingIds = new Set([...deletingIds, item.id]);

    // Optimistic remove
    removeItemFromLocal(item);

    router.delete(`/trash/${item.type}/${item.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        toast('Permanently deleted');
      },
      onError: () => {
        // Re-add the item on failure
        addItemToLocal(item);
        toast.error('Failed to delete item');
      },
      onFinish: () => {
        const next = new Set(deletingIds);
        next.delete(item.id);
        deletingIds = next;
      },
    });
  }

  function requestEmptyTrash() {
    confirmingEmptyTrash = true;
  }

  function confirmEmptyTrash() {
    confirmingEmptyTrash = false;

    router.delete('/trash/empty', {
      preserveScroll: true,
      onSuccess: () => {
        localProjects = [];
        localTasks = [];
        localEvents = [];
        toast('Trash emptied');
      },
      onError: () => {
        toast.error('Failed to empty trash');
      },
    });
  }

  function removeItemFromLocal(item: TrashItem) {
    switch (item.type) {
      case 'project':
        localProjects = localProjects.filter((p) => p.id !== item.id);
        break;
      case 'task':
        localTasks = localTasks.filter((t) => t.id !== item.id);
        break;
      case 'event':
        localEvents = localEvents.filter((e) => e.id !== item.id);
        break;
    }
  }

  function addItemToLocal(item: TrashItem) {
    switch (item.type) {
      case 'project':
        localProjects = [...localProjects, item];
        break;
      case 'task':
        localTasks = [...localTasks, item];
        break;
      case 'event':
        localEvents = [...localEvents, item];
        break;
    }
  }
</script>

<div class="trash-wrapper app-min-width">
  {#if $viewport.isMobile}
    <MobileTopBar title="Trash" back="/more" backLabel="Back to More" />
  {:else}
    <header class="trash-header">
      <div class="header-left">
        <Link href="/" class="logo-link">
          <img src={momentiumLogo} alt="Momentium" class="logo" />
          <span class="logo-text">Momentium</span>
        </Link>
        <span class="breadcrumb">/ Trash</span>
      </div>
      <div class="header-right">
        <ProfileDropdown />
      </div>
    </header>
  {/if}

  <main class="trash-page">
    <div class="trash-container">
      <div class="trash-title-row">
        <div>
          <h1 class="trash-title">
            <svg
              class="trash-title-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            Trash
          </h1>
          <p class="trash-subtitle">Items are permanently deleted after 30 days</p>
        </div>
        {#if !isEmpty}
          <button class="empty-trash-btn" onclick={requestEmptyTrash}> Empty Trash </button>
        {/if}
      </div>

      {#if isEmpty}
        <div class="empty-state">
          <svg
            class="empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
          <p class="empty-text">Trash is empty</p>
          <p class="empty-hint">Deleted projects, tasks, and events will appear here</p>
        </div>
      {:else}
        <div class="trash-list">
          {#each allItems as item (item.type + '-' + item.id)}
            <div
              class="trash-item"
              class:restoring={restoringIds.has(item.id)}
              class:deleting={deletingIds.has(item.id)}
            >
              <div class="item-icon-wrapper">
                <svg
                  class="item-type-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d={typeIcon(item.type)} />
                </svg>
              </div>
              <div class="item-info">
                <div class="item-title-row">
                  <span class="item-title">{item.title}</span>
                  <span class="item-type-badge">{typeLabel(item.type)}</span>
                </div>
                <div class="item-meta">
                  {#if item.type === 'project' && item.taskCount}
                    <span class="item-context"
                      >{item.taskCount} task{item.taskCount === 1 ? '' : 's'} included</span
                    >
                    <span class="meta-dot">·</span>
                  {/if}
                  {#if contextInfo(item)}
                    <span class="item-context">{contextInfo(item)}</span>
                    <span class="meta-dot">·</span>
                  {/if}
                  <span class="item-age">Deleted {daysAgo(item.discardedAt)}</span>
                  <span class="meta-dot">·</span>
                  <span class="item-remaining" class:urgent={item.daysRemaining <= 7}>
                    {item.daysRemaining} day{item.daysRemaining === 1 ? '' : 's'} remaining
                  </span>
                </div>
              </div>
              <div class="item-actions">
                <button
                  class="restore-btn"
                  onclick={() => restoreItem(item)}
                  disabled={restoringIds.has(item.id) || deletingIds.has(item.id)}
                  title={item.type === 'project' && item.taskCount
                    ? `Restore project and ${item.taskCount} task${item.taskCount === 1 ? '' : 's'}`
                    : 'Restore'}
                >
                  {#if restoringIds.has(item.id)}
                    Restoring...
                  {:else}
                    Restore
                  {/if}
                </button>
                <button
                  class="delete-btn"
                  onclick={() => requestDeleteItem(item)}
                  disabled={restoringIds.has(item.id) || deletingIds.has(item.id)}
                  title="Delete permanently"
                >
                  Delete
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </main>
</div>

<!-- Confirm permanent delete modal -->
{#if confirmingDelete}
  {@const deleteModal = useModalAnimation(() => !!confirmingDelete, cancelConfirm)}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={deleteModal.closing}
    bind:this={deleteModal.overlayEl}
    onclick={deleteModal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <span class="modal-header-icon">⚠️</span>
        <h2 class="modal-title">Delete permanently</h2>
      </div>
      <div class="modal-body">
        <p class="modal-item-name">"{confirmingDelete.title}"</p>
        {#if confirmingDelete.type === 'project' && confirmingDelete.taskCount}
          <p class="modal-text">
            This project has <strong
              >{confirmingDelete.taskCount} task{confirmingDelete.taskCount === 1
                ? ''
                : 's'}</strong
            > that will also be permanently deleted.
          </p>
        {/if}
        <p class="modal-text">
          This action is <strong>permanent and cannot be undone.</strong>
        </p>
      </div>
      <div class="modal-actions">
        <button type="button" class="modal-btn modal-btn-cancel" onclick={deleteModal.animateClose}>
          Cancel
        </button>
        <button type="button" class="modal-btn modal-btn-danger" onclick={confirmDeleteItem}>
          Delete Permanently
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Confirm empty trash modal -->
{#if confirmingEmptyTrash}
  {@const emptyModal = useModalAnimation(() => confirmingEmptyTrash, cancelEmptyTrash)}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={emptyModal.closing}
    bind:this={emptyModal.overlayEl}
    onclick={emptyModal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <span class="modal-header-icon modal-header-icon-trash"
          ><Icon name="trash" size={20} /></span
        >
        <h2 class="modal-title">Empty Trash</h2>
      </div>
      <div class="modal-body">
        <p class="modal-text">
          This will permanently delete <strong
            >{allItems.length} item{allItems.length === 1 ? '' : 's'}</strong
          >.
        </p>
        <div class="modal-implications">
          <p class="modal-implications-title">This action will:</p>
          <ul class="modal-implications-list">
            {#if localProjects.length > 0}
              <li>
                Delete {localProjects.length} project{localProjects.length === 1 ? '' : 's'} and all their
                tasks
              </li>
            {/if}
            {#if localTasks.length > 0}
              <li>Delete {localTasks.length} task{localTasks.length === 1 ? '' : 's'}</li>
            {/if}
            {#if localEvents.length > 0}
              <li>Delete {localEvents.length} event{localEvents.length === 1 ? '' : 's'}</li>
            {/if}
          </ul>
        </div>
        <p class="modal-text"><strong>This cannot be undone.</strong></p>
      </div>
      <div class="modal-actions">
        <button type="button" class="modal-btn modal-btn-cancel" onclick={emptyModal.animateClose}>
          Cancel
        </button>
        <button type="button" class="modal-btn modal-btn-danger" onclick={confirmEmptyTrash}>
          Empty Trash
        </button>
      </div>
    </div>
  </div>
{/if}

<svelte:window
  onkeydown={(e) => {
    if (e.key === 'Escape') {
      if (confirmingDelete) cancelConfirm();
      if (confirmingEmptyTrash) cancelEmptyTrash();
    }
  }}
/>

<style>
  .trash-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .trash-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.logo-link) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
    text-decoration: none;
  }

  .logo {
    height: 1.5rem;
    flex-shrink: 0;
  }

  .logo-text {
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
  }

  .breadcrumb {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .trash-page {
    padding: 2rem;
    max-width: 48rem;
    margin: 0 auto;
    width: 100%;
  }

  .trash-container {
    width: 100%;
  }

  .trash-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .trash-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.25rem;
  }

  .trash-title-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: #6b7280;
  }

  .trash-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .empty-trash-btn {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #dc2626;
    background: white;
    border: 1px solid #fca5a5;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .empty-trash-btn:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }

  /* Empty state */
  .empty-state {
    text-align: center;
    padding: 3rem 1.25rem;
    background: #f9fafb;
    border-radius: 0.75rem;
    border: 1px dashed #d1d5db;
  }

  .empty-icon {
    width: 3rem;
    height: 3rem;
    color: #d1d5db;
    margin: 0 auto 0.75rem;
    display: block;
  }

  .empty-text {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }

  .empty-hint {
    font-size: 0.8125rem;
    color: #9ca3af;
  }

  /* Trash list */
  .trash-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .trash-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    transition: all 0.15s;
  }

  .trash-item:hover {
    border-color: #d1d5db;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .trash-item.restoring,
  .trash-item.deleting {
    opacity: 0.5;
    pointer-events: none;
  }

  .item-icon-wrapper {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    border-radius: 0.375rem;
  }

  .item-type-icon {
    width: 1.125rem;
    height: 1.125rem;
    color: #6b7280;
  }

  .item-info {
    flex: 1;
    min-width: 0;
  }

  .item-title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.125rem;
  }

  .item-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-type-badge {
    flex-shrink: 0;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.1875rem;
    padding: 0 0.3125rem;
    line-height: 1.5;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .item-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .meta-dot {
    color: #d1d5db;
  }

  .item-context {
    color: #6b7280;
  }

  .item-age {
    color: #9ca3af;
  }

  .item-remaining {
    color: #9ca3af;
  }

  .item-remaining.urgent {
    color: #dc2626;
    font-weight: 500;
  }

  .item-actions {
    display: flex;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .restore-btn {
    padding: 0.3125rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #3b82f6;
    background: white;
    border: 1px solid #93c5fd;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .restore-btn:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .restore-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .delete-btn {
    padding: 0.3125rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #dc2626;
    background: white;
    border: 1px solid #fca5a5;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .delete-btn:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
  }

  .delete-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Modal styles (matching Settings.svelte pattern) */
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 28rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1.25rem 1.5rem 0;
  }

  .modal-header-icon {
    display: inline-flex;
    font-size: 1.25rem;
  }

  .modal-header-icon-trash {
    color: #dc2626;
  }

  .modal-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .modal-body {
    padding: 1rem 1.5rem 1.25rem;
  }

  .modal-item-name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
  }

  .modal-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }

  .modal-implications {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
  }

  .modal-implications-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #991b1b;
    margin-bottom: 0.375rem;
  }

  .modal-implications-list {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0;
  }

  .modal-implications-list li {
    font-size: 0.8125rem;
    color: #7f1d1d;
    line-height: 1.6;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .modal-btn {
    padding: 0.5rem 1.125rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .modal-btn-cancel {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
  }

  .modal-btn-cancel:hover {
    background: #f3f4f6;
  }

  .modal-btn-danger {
    background: #dc2626;
    border: 1px solid #dc2626;
    color: white;
  }

  .modal-btn-danger:hover {
    background: #b91c1c;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }

  /* Responsive */
  @media (max-width: 640px) {
    .trash-page {
      padding: 1rem;
    }

    .trash-item {
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .item-info {
      flex-basis: calc(100% - 3rem);
    }

    .item-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .item-meta {
      font-size: 0.6875rem;
    }
  }
</style>
